import CollectionPicker from '@app/components/collection-picker';
import { useBulkImportProducts } from '@shared/api/hooks/pcf/products';
import Text from '@shared/components/content/text';
import GuideLayout from '@shared/components/guide-layout';
import GuideTemplate from '@shared/components/guide-template';
import { Form } from '@shared/components/ui/form';
import { useToast } from '@shared/components/ui/use-toast';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { pcfRoutes } from '../../../pcf-routes';
import CsvImporterProducts from '../components/csv-importer-products';

type BulkImportForm = {
  collections: string[];
};

export default function BulkImportPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [importerOpen, setImporterOpen] = useState(false);

  const form = useForm<BulkImportForm>({
    defaultValues: { collections: [] },
  });

  const { mutateAsync: bulkImportProducts } = useBulkImportProducts();

  // Fired from the CSV importer modal's final submit step. The collections have
  // already been configured on the page (and created up-front by the picker),
  // so this kicks off the import directly with the product names extracted from
  // the uploaded file.
  const handleImport = async (productNames: string[]) => {
    if (!productNames.length) return;
    try {
      const response = await bulkImportProducts({
        data: {
          productNames,
          collectionUuids: form.getValues('collections'),
        },
      });
      toast({
        variant: 'success',
        title: 'Your products are being created',
        description: `We're importing ${response.queued} ${
          response.queued === 1 ? 'product' : 'products'
        } in the background. Refresh the page to see them as they're ready.`,
      });
      navigate(pcfRoutes.PRODUCTS);
    } catch (error) {
      setImporterOpen(false);
      toast({
        variant: 'destructive',
        title: 'Import failed',
        description:
          'We could not start your import. Please try again or contact support if the problem persists.',
      });
    }
  };

  return (
    <GuideLayout pageName="Quick Import" onClosePath={pcfRoutes.PRODUCTS}>
      <GuideTemplate
        className="w-full overflow-visible"
        preTitle="Quick import products"
        title="Organise your products"
        description={
          <div className="space-y-4">
            <Text>
              Quickly create products by uploading a list of product or recipe
              names. We&rsquo;ll auto-generate ingredients for each one so you
              have a draft assessment to start from.
            </Text>
            <Text>
              Group all imported products under one or more collections so
              they&rsquo;re easy to find and refine afterwards, or leave this
              blank to import without a collection.
            </Text>
          </div>
        }
        primaryActionProps={{
          onClick: () => setImporterOpen(true),
          'data-testid': 'bulk-import-begin-upload',
        }}
        primaryActionText="Begin upload"
        secondaryActionText="Back"
        secondaryActionProps={{
          onClick: () => navigate(pcfRoutes.PRODUCTS),
        }}
      >
        <Form {...form}>
          <div className="w-[390px] text-left">
            <Text className="mb-1.5 font-medium">
              Add to collections{' '}
              <span className="ml-1 font-normal text-gray-600">(optional)</span>
            </Text>
            <CollectionPicker name="collections" />
          </div>
        </Form>
      </GuideTemplate>

      <CsvImporterProducts
        isOpen={importerOpen}
        onClose={() => setImporterOpen(false)}
        onSubmit={handleImport}
      />
    </GuideLayout>
  );
}
